import { Conditional } from '@/lib/components/core/conditional/conditional';
import { Form, InputNumber, Input, Typography, Row, Col } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../page.module.scss';

interface LozingenInputDto {
  betrekkingTotTeeltwijze: number;
  lozingen: {
    periode: number;
    hoeveelheid?: number;
    toegediendVoedingswaterHoeveelheid?: number;
    hergebruiktDrainagewaterHoeveelheid?: number;
  }[];
}

interface Properties {
  teeltwijze: number;
}

export const LozingenV2: FC<Properties> = ({ teeltwijze }) => {
  const { t } = useTranslation('input');
  const { Title } = Typography;
  const form = Form.useFormInstance();

  const [lozingenList, setLozingenList] = useState<LozingenInputDto[]>([]);

  useEffect(() => {
    const existingLozingen = form.getFieldValue('lozingen') || [];

    const hasLozing = existingLozingen.some((l: LozingenInputDto) => l.betrekkingTotTeeltwijze === teeltwijze);

    if (hasLozing) {
      setLozingenList(existingLozingen);
    } else {
      const newLozingenInputDto: LozingenInputDto = {
        betrekkingTotTeeltwijze: teeltwijze,
        lozingen: Array.from({ length: 13 }, (_, index) => ({
          periode: index + 1,
          hoeveelheid: undefined,
        })),
      };
      const updatedLozingen = [...existingLozingen, newLozingenInputDto];

      form.setFieldsValue({
        lozingen: updatedLozingen,
      });

      setLozingenList(updatedLozingen);
    }
  }, [teeltwijze, form]);

  const currentLozingIndex = lozingenList.findIndex(l => l.betrekkingTotTeeltwijze === teeltwijze);

  const data = useMemo(() => Array.from({ length: 13 }, (_, i) => ({ key: i, periode: i + 1 })), []);

  const listCols = teeltwijze === 0 ? { periode: 2, hoeveelheid: 2, voedingswater: 2, hergebruikt: 2 } : { periode: 2, hoeveelheid: 3 };

  return (
    <>
      <Title style={{ margin: '10px 0px' }} level={5}>
        <Conditional condition={teeltwijze === 0}>{t('form.emissionReport.volumes.titleGrondteelt')}</Conditional>
        <Conditional condition={teeltwijze === 1}>{t('form.emissionReport.volumes.titleSubstraatteelt')}</Conditional>
      </Title>
      <Typography style={{ marginBottom: '10px' }}>
        <Conditional condition={teeltwijze === 0}>{t('form.emissionReport.volumes.typeGrondteelt')}</Conditional>
        <Conditional condition={teeltwijze === 1}>{t('form.emissionReport.volumes.typeSubstraatteelt')}</Conditional>
      </Typography>
      <Conditional condition={currentLozingIndex !== -1}>
        <>
          <Form.Item name={['lozingen', currentLozingIndex, 'betrekkingTotTeeltwijze']} initialValue={teeltwijze} hidden>
            <Input />
          </Form.Item>

          <Row style={{ fontWeight: 'bold', marginBottom: 4 }}>
            <Col span={listCols.periode} style={{ minWidth: 80 }}>
              {t('form.emissionReport.metingen.headers.period')}
            </Col>
            <Col span={listCols.hoeveelheid} style={{ minWidth: 100 }}>
              Lozing hoeveelheid
            </Col>
            <Conditional condition={teeltwijze === 0}>
              <>
                <Col span={listCols.voedingswater} style={{ minWidth: 100 }}>
                  Toegediend voedingswater
                </Col>
                <Col span={listCols.hergebruikt} style={{ minWidth: 100 }}>
                  Hergebruikt drainagewater
                </Col>
              </>
            </Conditional>
          </Row>
          {data.map(({ key, periode }, index) => (
            <Form.Item key={key} noStyle shouldUpdate={(prev, curr) => prev !== curr}>
              {() => (
                <Row className={styles.rowBorder} style={{ padding: 4 }}>
                  <Col span={listCols.periode} style={{ minWidth: 80 }}>
                    <Form.Item name={['lozingen', currentLozingIndex, 'lozingen', index, 'periode']} initialValue={periode}>
                      <span>{periode}</span>
                    </Form.Item>
                  </Col>
                  <Col span={listCols.hoeveelheid} style={{ minWidth: 100 }}>
                    <Form.Item
                      name={['lozingen', currentLozingIndex, 'lozingen', index, 'hoeveelheid']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: '' }]}>
                      <InputNumber min={0} decimalSeparator={','} precision={0} step={1} max={99999999.9} style={{ width: '90%' }} />
                    </Form.Item>
                  </Col>
                  <Conditional condition={teeltwijze === 0}>
                    <>
                      <Col span={listCols.voedingswater} style={{ minWidth: 100 }}>
                        <Form.Item name={['lozingen', currentLozingIndex, 'lozingen', index, 'toegediendVoedingswaterHoeveelheid']}>
                          <InputNumber min={0} decimalSeparator={','} precision={0} step={1} max={99999999.9} style={{ width: '90%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={listCols.hergebruikt} style={{ minWidth: 100 }}>
                        <Form.Item
                          name={['lozingen', currentLozingIndex, 'lozingen', index, 'hergebruiktDrainagewaterHoeveelheid']}
                          style={{ marginBottom: 0 }}>
                          <InputNumber min={0} decimalSeparator={','} precision={0} step={1} max={99999999.9} style={{ width: '90%' }} />
                        </Form.Item>
                      </Col>
                    </>
                  </Conditional>
                </Row>
              )}
            </Form.Item>
          ))}
        </>
      </Conditional>
    </>
  );
};
